import {
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableRow,
  Box,
  Button,
  TextField,
  ButtonGroup,
  TableHead,
  MenuItem,
  IconButton,
} from "@mui/material";

import React, { useState, useEffect, useMemo } from "react";

import GolfCourseIcon from "@mui/icons-material/GolfCourse";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import DrillingAddProgressFormModal from "./DrillingAddProgressFormModal";
import DrillingProgressViewModal from "./DrillingProgressViewModal";

import api from "../../api/axios";
import dayjs from "dayjs";

export default function DrillingTemplate({ selectedProject }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf("month"));
  const [progressData, setProgressData] = useState({});

  /* ================= BOREHOLE FORM ================= */

  const [bhCode, setBhCode] = useState("");
  const [bhError, setBhError] = useState("");

  const [selectedRig, setSelectedRig] = useState("");
  const [rigs, setRigs] = useState([]);
  const [rigLoading, setRigLoading] = useState(false);
  const [rigError, setRigError] = useState("");

  /* ================= MODALS ================= */

  const [openModal, setOpenModal] = useState(false);
  const [openProgressView, setOpenProgressView] = useState(false);

  const [selectedBH, setSelectedBH] = useState({
    id: null,
    borehole_code: "",
    rig_name: "",
    meterage: 0,
  });

  /* ================= FETCH DRILLING DATA ================= */

  useEffect(() => {
    const fetchDrillingData = async () => {
      try {
        const { data: response } = await api.get(
          `/projects/exploration/progress/${selectedProject.id}`,
        );

        setProgressData(response.data);
      } catch (err) {
        console.error("Failed to fetch drilling progress data:", err);
      }
    };

    if (selectedProject?.id) {
      fetchDrillingData();
    }
  }, [refreshKey, selectedProject]);

  /* ================= FETCH ACTIVE RIGS ================= */

  useEffect(() => {
    const fetchActiveRigs = async () => {
      try {
        setRigLoading(true);
        setRigError("");

        const { data: response } = await api.get(
          "/projects/exploration/get_active_rigs",
        );

        const rigData = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

        setRigs(rigData);
      } catch (err) {
        console.error("Failed to fetch active rigs:", err);
        setRigError("Failed to load rigs");
        setRigs([]);
      } finally {
        setRigLoading(false);
      }
    };

    fetchActiveRigs();
  }, []);

  /* ================= PROJECT DRILLING SUMMARY ================= */

  const projectProgress = Array.isArray(progressData.boreholeProgress)
    ? progressData.boreholeProgress
    : [];

  // Get unique progress dates
  const uniqueProgressDates = [
    ...new Set(
      projectProgress
        .filter((row) => row.date)
        .map((row) => dayjs(row.date).format("YYYY-MM-DD")),
    ),
  ].sort();

  // First and last progress dates
  const firstProgressDate =
    uniqueProgressDates.length > 0 ? dayjs(uniqueProgressDates[0]) : null;

  const lastProgressDate =
    uniqueProgressDates.length > 0
      ? dayjs(uniqueProgressDates[uniqueProgressDates.length - 1])
      : null;

  // Duration = difference between first and last progress date
  const durationDays =
    firstProgressDate && lastProgressDate
      ? lastProgressDate.diff(firstProgressDate, "day")
      : 0;

  // Idle days = days within duration where no progress was recorded
  const idleDays =
    durationDays > 0
      ? Math.max(durationDays - (uniqueProgressDates.length - 1), 0)
      : 0;

  // Overall average meterage per duration day
  const totalProjectMeterage = projectProgress.reduce(
    (total, row) => total + Number(row.meterage || 0),
    0,
  );

  const overallAveragePerDay =
    durationDays > 0 ? totalProjectMeterage / durationDays : 0;

  /* ================= ADD BOREHOLE ================= */

  const addBorehole = async () => {
    if (!bhCode || bhCode.trim() === "") {
      setBhError("Please enter a valid Borehole Code");
      return;
    }

    if (!selectedRig) {
      setRigError("Please select a rig");
      return;
    }

    try {
      await api.post(
        "/projects/exploration/add_borehole/" + progressData.project_id,
        {
          boreholeNumber: bhCode.trim(),
          rig_id: selectedRig,
        },
      );

      setBhCode("");
      setSelectedRig("");
      setBhError("");
      setRigError("");

      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to add borehole:", err);

      const message = err?.response?.data?.message || "Failed to add borehole";

      alert(message);
    }
  };

  /* ================= VIEW PROGRESS ================= */

  const handleViewClick = (bh_id, bh_code, rig_name) => () => {
    setSelectedBH({
      id: bh_id,
      borehole_code: bh_code,
      rig_name: rig_name || "",
    });

    setOpenProgressView(true);
  };

  /* ================= MONTHLY PROGRESS ================= */

  const monthlyProgress = useMemo(() => {
    const monthStart = selectedMonth.startOf("month");
    const monthEnd = selectedMonth.endOf("month");

    const boreholes = Array.isArray(progressData.boreholeData)
      ? progressData.boreholeData
      : [];

    const progressRecords = Array.isArray(progressData.boreholeProgress)
      ? progressData.boreholeProgress
      : [];

    const boreholeMap = new Map(boreholes.map((bh) => [Number(bh.id), bh]));

    // Get progress records belonging to the selected month
    const monthlyRecords = progressRecords.filter((record) => {
      if (!record.date) return false;

      const recordDate = dayjs(record.date);

      return (
        recordDate.isValid() &&
        !recordDate.isBefore(monthStart, "day") &&
        !recordDate.isAfter(monthEnd, "day")
      );
    });

    const boreholeTotals = new Map();

    monthlyRecords.forEach((record) => {
      const bhId = Number(record.bh_id);
      const borehole = boreholeMap.get(bhId);

      if (!borehole) return;

      const closureDate = borehole.closure_date
        ? dayjs(borehole.closure_date)
        : null;

      // Determine status as of selected month end.
      // A borehole closed after the selected month
      // was still in progress during that month.
      const closedByMonthEnd =
        closureDate?.isValid() && !closureDate.isAfter(monthEnd);

      const isAbandoned =
        (borehole.isAbandoned === true || Number(borehole.isAbandoned) === 1) &&
        closedByMonthEnd;

      const existing = boreholeTotals.get(bhId) || {
        id: bhId,
        code: borehole.bh_code,
        rig: borehole.rig_name || "-",
        monthlyMeterage: 0,
        totalDepth: Number(borehole.meterage || 0),
        isClosed: Boolean(closedByMonthEnd) && !isAbandoned,
        isAbandoned,
      };

      existing.monthlyMeterage += Number(record.meterage || 0);

      boreholeTotals.set(bhId, existing);
    });

    const monthlyBoreholes = [...boreholeTotals.values()];

    const closed = monthlyBoreholes.filter((bh) => bh.isClosed);

    const inProgress = monthlyBoreholes.filter(
      (bh) => !bh.isClosed && !bh.isAbandoned,
    );

    const abandoned = monthlyBoreholes.filter((bh) => bh.isAbandoned);

    const sum = (items, field) =>
      items.reduce((total, bh) => total + Number(bh[field] || 0), 0);

    return {
      total: sum(monthlyBoreholes, "monthlyMeterage"),

      closed: {
        monthlyProgress: sum(closed, "monthlyMeterage"),
        totalDepth: sum(closed, "totalDepth"),
        boreholes: closed,
      },

      inProgress: {
        total: sum(inProgress, "monthlyMeterage"),
        boreholes: inProgress,
      },

      abandoned: {
        total: sum(abandoned, "monthlyMeterage"),
        boreholes: abandoned,
      },
    };
  }, [progressData, selectedMonth]);

  /* ================= HELPERS ================= */

  const formatMeterage = (value) => Number(value || 0).toFixed(2);

  const changeMonth = (amount) => {
    setSelectedMonth((current) => current.add(amount, "month"));
  };

  const currentMonth = dayjs().startOf("month");

  /* ================= SHARED GRID STYLES ================= */

  const closedGridColumns =
    "minmax(65px, 1fr) minmax(75px, 1.3fr) minmax(75px, 1fr) minmax(75px, 1fr)";

  const inProgressGridColumns =
    "minmax(65px, 1fr) minmax(75px, 1.3fr) minmax(90px, 1.2fr)";

  const gridHeaderStyle = {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "text.secondary",
    whiteSpace: "nowrap",
  };

  const gridCellStyle = {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.75rem",
  };

  return (
    <>
      {/* ========================================================= */}
      {/* MASTER DATA */}
      {/* ========================================================= */}

      <Grid container spacing={2}>
        <Grid size={6}>
          <Card>
            <CardActionArea>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {progressData.masterData?.borehole_count ?? "--"}
                </Typography>

                <Typography variant="body1" color="text.secondary">
                  Boreholes
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid size={6}>
          <Card>
            <CardActionArea>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {progressData.masterData?.total_meterage != null
                    ? Number(progressData.masterData.total_meterage).toFixed(2)
                    : "--"}
                </Typography>

                <Typography variant="body1" color="text.secondary">
                  Total Meterage
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      {/* ========================================================= */}
      {/* PROJECT DRILLING SUMMARY */}
      {/* ========================================================= */}

      <Grid container spacing={1.5} sx={{ mt: 2 }}>
        {/* DURATION */}
        <Grid size={4}>
          <Card>
            <CardActionArea>
              <CardContent
                sx={{
                  textAlign: "center",
                  py: 1.2,
                  px: 1,
                  "&:last-child": { pb: 1.2 },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ lineHeight: 1.2 }}
                >
                  {durationDays}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  Total Days
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        {/* IDLE DAYS */}
        <Grid size={4}>
          <Card>
            <CardActionArea>
              <CardContent
                sx={{
                  textAlign: "center",
                  py: 1.2,
                  px: 1,
                  "&:last-child": { pb: 1.2 },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ lineHeight: 1.2 }}
                >
                  {idleDays}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  Idle Days
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        {/* OVERALL AVERAGE */}
        <Grid size={4}>
          <Card>
            <CardActionArea>
              <CardContent
                sx={{
                  textAlign: "center",
                  py: 1.2,
                  px: 1,
                  "&:last-child": { pb: 1.2 },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ lineHeight: 1.2 }}
                >
                  {overallAveragePerDay.toFixed(2)}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.7rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  Avg. m/day
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      {/* ========================================================= */}
      {/* MONTHLY PROGRESS SECTION */}
      {/* ========================================================= */}

      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(46,125,50,0.12), rgba(255,255,255,0.025))",
          border: "1px solid rgba(255,255,255,0.09)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <IconButton
            size="small"
            onClick={() => changeMonth(-1)}
            aria-label="Previous month"
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ textAlign: "center", minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ lineHeight: 1.3 }}
            >
              Monthly Progress
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {selectedMonth.format("MMMM YYYY")}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={() => changeMonth(1)}
            disabled={!selectedMonth.isBefore(currentMonth, "month")}
            aria-label="Next month"
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>

        {/* TOTAL METERAGE */}
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            textAlign: "center",
            background: "rgba(46,125,50,0.15)",
            border: "1px solid rgba(76,175,80,0.25)",
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Total Meterage
          </Typography>

          <Typography
            variant="h4"
            fontWeight={800}
            sx={{
              color: "#66BB6A",
              lineHeight: 1.3,
            }}
          >
            {formatMeterage(monthlyProgress.total)} m
          </Typography>
        </Box>

        {/* ========================================================= */}
        {/* CLOSED BOREHOLES */}
        {/* ========================================================= */}

        <Box
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 2,
            background: "rgba(33,150,243,0.07)",
            border: "1px solid rgba(33,150,243,0.2)",
          }}
        >
          {/* SECTION HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleOutlineIcon sx={{ color: "#42A5F5", fontSize: 20 }} />

              <Typography variant="body2" fontWeight={700}>
                Closed Boreholes
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  color: "text.secondary",
                }}
              >
                Total Borehole Depth
              </Typography>

              <Typography
                variant="h6"
                fontWeight={800}
                sx={{
                  color: "#42A5F5",
                  lineHeight: 1.2,
                }}
              >
                {formatMeterage(monthlyProgress.closed.totalDepth)} m
              </Typography>
            </Box>
          </Box>

          {/* CLOSED BOREHOLES GRID */}
          {monthlyProgress.closed.boreholes.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No closed borehole progress this month
            </Typography>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: closedGridColumns,
                columnGap: 1,
                alignItems: "center",
              }}
            >
              {/* COLUMN HEADERS */}
              {["Borehole", "Rig", "Monthly Progress", "Total Depth"].map(
                (label, index) => (
                  <Typography
                    key={label}
                    sx={{
                      ...gridHeaderStyle,
                      textAlign: index >= 2 ? "right" : "left",
                      ...(index === 3 && {
                        color: "#64B5F6",
                      }),
                    }}
                  >
                    {label}
                  </Typography>
                ),
              )}

              {/* BOREHOLE ROWS */}
              {monthlyProgress.closed.boreholes.map((bh) => (
                <React.Fragment key={bh.id}>
                  {/* BOREHOLE */}
                  <Typography
                    sx={{
                      ...gridCellStyle,
                      py: 1.2,
                      fontWeight: 700,
                    }}
                    title={bh.code}
                  >
                    {bh.code}
                  </Typography>

                  {/* RIG */}
                  <Typography
                    sx={{
                      ...gridCellStyle,
                      color: "text.secondary",
                    }}
                    title={bh.rig}
                  >
                    {bh.rig}
                  </Typography>

                  {/* MONTHLY PROGRESS */}
                  <Typography
                    sx={{
                      ...gridCellStyle,
                      textAlign: "right",
                      color: "text.secondary",
                    }}
                  >
                    {formatMeterage(bh.monthlyMeterage)} m
                  </Typography>

                  {/* TOTAL DEPTH */}
                  <Typography
                    sx={{
                      ...gridCellStyle,
                      textAlign: "right",
                      fontWeight: 800,
                      color: "#42A5F5",
                    }}
                  >
                    {formatMeterage(bh.totalDepth)} m
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          )}

          {/* TOTAL ROW */}
          {monthlyProgress.closed.boreholes.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: closedGridColumns,
                columnGap: 1,
                alignItems: "center",
                pt: 1.2,
                borderTop: "1px solid rgba(66,165,245,0.3)",
              }}
            >
              <Typography
                sx={{
                  gridColumn: "1 / 3",
                  fontSize: "0.75rem",
                  fontWeight: 800,
                }}
              >
                TOTAL
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textAlign: "right",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                {formatMeterage(monthlyProgress.closed.monthlyProgress)} m
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 900,
                  textAlign: "right",
                  color: "#42A5F5",
                  whiteSpace: "nowrap",
                }}
              >
                {formatMeterage(monthlyProgress.closed.totalDepth)} m
              </Typography>
            </Box>
          )}
        </Box>

        {/* ========================================================= */}
        {/* IN-PROGRESS BOREHOLES */}
        {/* ========================================================= */}

        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            background: "rgba(255,193,7,0.06)",
            border: "1px solid rgba(255,193,7,0.2)",
          }}
        >
          {/* SECTION HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <TimelapseIcon sx={{ color: "#FFCA28", fontSize: 20 }} />

              <Typography variant="body2" fontWeight={700}>
                In-progress Boreholes
              </Typography>
            </Box>

            <Typography
              variant="h6"
              fontWeight={800}
              sx={{
                color: "#FFCA28",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              {formatMeterage(monthlyProgress.inProgress.total)} m
            </Typography>
          </Box>

          {/* IN-PROGRESS BOREHOLES GRID */}
          {monthlyProgress.inProgress.boreholes.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No in-progress borehole activity this month
            </Typography>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: inProgressGridColumns,
                columnGap: 1,
                alignItems: "center",
              }}
            >
              {/* COLUMN HEADERS */}
              {["Borehole", "Rig", "Monthly Progress"].map((label, index) => (
                <Typography
                  key={label}
                  sx={{
                    ...gridHeaderStyle,
                    textAlign: index === 2 ? "right" : "left",
                  }}
                >
                  {label}
                </Typography>
              ))}

              {/* BOREHOLE ROWS */}
              {monthlyProgress.inProgress.boreholes.map((bh) => (
                <React.Fragment key={bh.id}>
                  {/* BOREHOLE */}
                  <Typography
                    sx={{
                      ...gridCellStyle,
                      py: 1.2,
                      fontWeight: 700,
                    }}
                    title={bh.code}
                  >
                    {bh.code}
                  </Typography>

                  {/* RIG */}
                  <Typography
                    sx={{
                      ...gridCellStyle,
                      color: "text.secondary",
                    }}
                    title={bh.rig}
                  >
                    {bh.rig}
                  </Typography>

                  {/* MONTHLY PROGRESS */}
                  <Typography
                    sx={{
                      ...gridCellStyle,
                      textAlign: "right",
                      fontWeight: 700,
                      color: "#FFB74D",
                    }}
                  >
                    {formatMeterage(bh.monthlyMeterage)} m
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>

        {/* ========================================================= */}
        {/* ABANDONED BOREHOLES */}
        {/* ========================================================= */}

        {monthlyProgress.abandoned.boreholes.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 2,
              background: "rgba(244,67,54,0.06)",
              border: "1px solid rgba(244,67,54,0.2)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: "#EF5350" }}
              >
                Abandoned Boreholes
              </Typography>

              <Typography
                variant="subtitle2"
                fontWeight={800}
                sx={{ color: "#EF5350", whiteSpace: "nowrap" }}
              >
                {formatMeterage(monthlyProgress.abandoned.total)} m
              </Typography>
            </Box>

            {monthlyProgress.abandoned.boreholes.map((bh) => (
              <Box
                key={bh.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 90px",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.75,
                  pl: 1,
                  borderLeft: "2px solid rgba(239,83,80,0.4)",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#EF5350",
                      fontWeight: 600,
                    }}
                  >
                    {bh.code}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={bh.rig}
                  >
                    {bh.rig}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  fontWeight={700}
                  textAlign="right"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "#EF5350",
                  }}
                >
                  {formatMeterage(bh.monthlyMeterage)} m
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* ========================================================= */}
      {/* ADD BOREHOLE SECTION */}
      {/* ========================================================= */}

      <Box
        sx={{
          mt: 2,
          backgroundColor: "background.paper",
          borderRadius: 2,
          p: 1,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* SELECT RIG */}
        <TextField
          select
          label="Select Rig"
          size="small"
          value={selectedRig}
          onChange={(e) => {
            setSelectedRig(e.target.value);

            if (e.target.value) {
              setRigError("");
            }
          }}
          error={Boolean(rigError)}
          helperText={rigError}
          disabled={rigLoading}
          fullWidth
          sx={{
            mb: 1,
            "& .MuiInputBase-root": {
              minHeight: 40,
              height: 40,
            },
            "& .MuiFormHelperText-root": {
              marginTop: 0.25,
            },
          }}
        >
          {rigLoading ? (
            <MenuItem disabled>Loading rigs...</MenuItem>
          ) : rigs.length === 0 ? (
            <MenuItem disabled>No active rigs available</MenuItem>
          ) : (
            rigs.map((rig) => (
              <MenuItem key={rig.id} value={rig.id}>
                {rig.rig_name}
                {Number(rig.is_third_party) === 1
                  ? " (Third Party)"
                  : " (SPPL Owned)"}
              </MenuItem>
            ))
          )}
        </TextField>

        {/* BOREHOLE CODE + ADD BUTTON */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            width: "100%",
          }}
        >
          <TextField
            label="Enter Borehole Code"
            name="boreholeNumber"
            size="small"
            value={bhCode}
            onChange={(e) => {
              setBhCode(e.target.value);

              if (e.target.value.trim() !== "") {
                setBhError("");
              }
            }}
            error={Boolean(bhError)}
            helperText={bhError}
            onBlur={() => {
              if (!bhCode.trim()) {
                setBhError("Please Insert Valid Borehole Code.");
              }
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              "& .MuiInputBase-root": {
                minHeight: 40,
                height: 40,
              },
              "& .MuiFormHelperText-root": {
                marginTop: 0.25,
              },
            }}
          />

          <Button
            variant="outlined"
            startIcon={<GolfCourseIcon />}
            onClick={addBorehole}
            disabled={!bhCode.trim() || !selectedRig || rigLoading}
            sx={{
              whiteSpace: "nowrap",
              px: 1.5,
              minHeight: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            Add New BH
          </Button>
        </Box>
      </Box>

      {/* ========================================================= */}
      {/* BOREHOLE LIST */}
      {/* ========================================================= */}

      <TableContainer
        component={Paper}
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          overflow: "hidden",
          mt: 2,
          width: "100%",
        }}
      >
        <Table
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: "20%",
                  fontWeight: 600,
                  textAlign: "center",
                  px: 0.5,
                  py: 1,
                  fontSize: "0.8rem",
                }}
              >
                Borehole
              </TableCell>

              <TableCell
                sx={{
                  width: "20%",
                  fontWeight: 600,
                  textAlign: "center",
                  px: 0.5,
                  py: 1,
                  fontSize: "0.8rem",
                }}
              >
                Meterage
              </TableCell>

              <TableCell
                sx={{
                  width: "25%",
                  fontWeight: 600,
                  textAlign: "center",
                  px: 0.5,
                  py: 1,
                  fontSize: "0.8rem",
                }}
              >
                Rig
              </TableCell>

              <TableCell
                sx={{
                  width: "35%",
                  fontWeight: 600,
                  textAlign: "center",
                  px: 0.5,
                  py: 1,
                  fontSize: "0.8rem",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {progressData.boreholeData == null ||
            progressData.boreholeData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    py: 3,
                    color: "text.secondary",
                    fontSize: "0.8rem",
                  }}
                >
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              [...progressData.boreholeData]
                .sort((a, b) => a.id - b.id)
                .map((row) => {
                  const isClosed = Boolean(row.closure_date);
                  const isAbandoned = Number(row.isAbandoned) === 1;

                  const rigName = row.rig_name || "-";

                  const truncatedRigName =
                    rigName.length > 15
                      ? `${rigName.substring(0, 15)}...`
                      : rigName;

                  const holeProgress =
                    progressData.boreholeProgress?.filter(
                      (p) => Number(p.bh_id) === Number(row.id),
                    ) || [];

                  const progressDays = new Set(
                    holeProgress.map((p) => dayjs(p.date).format("YYYY-MM-DD")),
                  ).size;

                  const averagePerDay =
                    progressDays > 0
                      ? Number(row.meterage || 0) / progressDays
                      : 0;

                  return (
                    <TableRow key={row.id} hover>
                      {/* BOREHOLE */}
                      <TableCell
                        sx={{
                          textAlign: "center",
                          px: 0.5,
                          py: 0.8,
                          fontSize: "0.8rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: isAbandoned ? "error.main" : "inherit",
                          fontWeight: isAbandoned ? 600 : 400,
                        }}
                      >
                        {row.bh_code}
                      </TableCell>

                      {/* METERAGE */}
                      <TableCell
                        sx={{
                          textAlign: "center",
                          px: 0.5,
                          py: 0.8,
                          fontSize: "0.8rem",
                          whiteSpace: "nowrap",
                          color: isAbandoned ? "error.main" : "inherit",
                          fontWeight: isAbandoned ? 600 : 400,
                        }}
                      >
                        {row.meterage != null
                          ? Number(row.meterage).toFixed(2)
                          : "0.00"}
                      </TableCell>

                      {/* RIG */}
                      <TableCell
                        sx={{
                          textAlign: "center",
                          px: 0.5,
                          py: 0.8,
                          fontSize: "0.75rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={rigName}
                      >
                        {truncatedRigName}

                        <Typography
                          variant="caption"
                          display="block"
                          sx={{
                            mt: 0.2,
                            fontSize: "0.6rem",
                            lineHeight: 1.2,
                            color: "#2196F3",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Avg: {averagePerDay.toFixed(2)} m/day
                        </Typography>
                      </TableCell>

                      {/* ACTION */}
                      <TableCell
                        sx={{
                          textAlign: "center",
                          px: 0.3,
                          py: 0.5,
                        }}
                      >
                        <ButtonGroup
                          variant="text"
                          size="small"
                          aria-label="Borehole actions"
                          sx={{
                            "& .MuiButton-root": {
                              minWidth: 0,
                              px: 0.8,
                              py: 0.4,
                              fontSize: "0.7rem",
                              whiteSpace: "nowrap",
                            },
                          }}
                        >
                          <Button
                            disabled={isClosed}
                            onClick={() => {
                              setSelectedBH({
                                id: row.id,
                                borehole_code: row.bh_code,
                                rig_name: row.rig_name || "",
                              });

                              setOpenModal(true);
                            }}
                          >
                            {isClosed
                              ? isAbandoned
                                ? "Abandoned"
                                : "Closed"
                              : "Add"}
                          </Button>

                          <Button
                            onClick={handleViewClick(
                              row.id,
                              row.bh_code,
                              row.rig_name,
                            )}
                          >
                            View
                          </Button>
                        </ButtonGroup>

                        {/* CLOSURE / ABANDONMENT DATE */}
                        {isClosed && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              mt: 0.2,
                              fontSize: "0.6rem",
                              lineHeight: 1.2,
                              color: "error.main",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isAbandoned ? "Abandoned on: " : "Closed: "}
                            {dayjs(row.closure_date).format("DD-MM-YYYY")}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ========================================================= */}
      {/* ADD PROGRESS MODAL */}
      {/* ========================================================= */}

      <DrillingAddProgressFormModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        projectName={selectedProject.project_name}
        borehole={selectedBH}
        projectId={progressData.project_id}
        onSuccess={() => setRefreshKey((prev) => prev + 1)}
      />

      {/* ========================================================= */}
      {/* VIEW PROGRESS MODAL */}
      {/* ========================================================= */}

      <DrillingProgressViewModal
        open={openProgressView}
        onClose={() => setOpenProgressView(false)}
        projectName={selectedProject.project_name}
        borehole={selectedBH}
        progressData={progressData.boreholeProgress?.filter(
          (bh) => Number(bh.bh_id) === Number(selectedBH.id),
        )}
      />
    </>
  );
}
